import React, { useEffect, useState } from 'react';
import useAuth from '../hooks/useAuth';
import { useParams, useNavigate } from 'react-router-dom';
import { Link } from 'react-router-dom';

function renderBody(body_text, mediaArray) {
  // show body_text first
  const renderText = () => {
    if (!body_text) return null;
    return <p className="mt-2 text-slate-700">{body_text}</p>;
  };

  const renderMedia = () => {
    if (!mediaArray) return null;
    let media = mediaArray;
    if (typeof mediaArray === 'string') {
      try { media = JSON.parse(mediaArray); } catch (e) { media = []; }
    }
    if (!Array.isArray(media) || media.length === 0) return null;
    // render first media (gallery support can be added later)
    const m = media[0];
    if (!m || !m.url) return null;
    return (
      <div className="mt-3 w-full rounded-md bg-slate-100">
        {m.caption && <div className="p-2">{m.caption}</div>}
        <div className="px-2">
          <a href={m.url} target="_blank" rel="noopener noreferrer" className="inline-block w-full">
            <img src={m.url} alt={m.alt || ''} className="w-full h-auto object-contain max-h-[80vh] rounded-md" />
          </a>
        </div>
      </div>
    );
  };

  return (
    <>
      {renderText()}
      {renderMedia()}
    </>
  );
}

function renderCommentText(text) {
  if (text === null || text === undefined) return null;
  // Prefer to show a removal placeholder when comment text encodes a removal marker
  if (typeof text === 'string') {
    const trimmed = text.trim();
    if (trimmed === '[REMOVED BY USER]') {
      return <div className="mt-2 italic text-sm text-slate-500">Comment removed</div>;
    }
    // try parse JSON removal markers like '{"removed":true, "reason":"..."}'
  if (trimmed.startsWith('{') || trimmed.startsWith('"{')) {
      try {
        const parsed = JSON.parse(trimmed);
        if (parsed && parsed.removed) {
          const reason = parsed.reason || parsed.removal_reason || parsed.removed_reason || null;
          if (reason) return <div className="mt-2 italic text-sm text-slate-500">Comment removed: <span className="text-slate-400">{reason}</span></div>;
          return <div className="mt-2 italic text-sm text-slate-500">Comment removed</div>;
        }
      } catch (e) {
        // not JSON — fallthrough to render raw text
      }
    }
  }
  return <div className="mt-2 break-words" style={{ overflowWrap: 'anywhere', wordBreak: 'break-word' }}>{String(text)}</div>;
}

// Stable recursive comment renderer (defined outside the main component so its identity doesn't change between renders)
function CommentNode(props) {
  const {
    comment, replyingTo, setReplyingTo, deleteComment,
    replyText, setReplyText, submitReply, voteComment,
    commentVotes, currentUser, threadAuthorId, depth = 0,
    parentPad = 0 // absolute padLeft of parent (px) to avoid cumulative padding
  } = props;

  // Visual knobs
  const INDENT_STEP = 12;      // px per level (visual only)
  // After this depth, do NOT increase horizontal offset any further.
  // Top-level comment is depth=0; cap at depth 3 so further replies don't shift right.
  const INDENT_CAP_LEVEL = 3;  // stop increasing padding after this depth
  const COLLAPSE_AT = 4;

  // Compute a safe, non-growing indent: padding-left on the row
  const cappedLevel = Math.min(depth, INDENT_CAP_LEVEL);
  const padLeft = cappedLevel * INDENT_STEP; // absolute desired pad for this node
  // To avoid cumulative indentation (child padding adding on top of parent padding), compute the local padding
  // applied inside this element relative to the parent's absolute pad.
  const localPad = Math.max(0, padLeft - (Number(parentPad) || 0));

  const [expanded, setExpanded] = useState(false);
  const [collapsed, setCollapsed] = useState(false);

  const countDescendants = (node) => {
    if (!node?.replies?.length) return 0;
    let n = node.replies.length;
    for (const r of node.replies) n += countDescendants(r);
    return n;
    };

  // determine if this comment is a removal marker
  const isRemoved = (() => {
    const t = comment?.text;
    if (t === null || t === undefined) return false;
    if (typeof t !== 'string') return false;
    const trimmed = t.trim();
    if (trimmed === '[REMOVED BY USER]') return true;
    if (trimmed.startsWith('{') || trimmed.startsWith('"{')) {
      try {
        const parsed = JSON.parse(trimmed);
        if (parsed && parsed.removed) return true;
      } catch (e) {
        // not JSON
      }
    }
    return false;
  })();

  return (
    <div className="border rounded-md bg-white">
      <div
        className="p-3"
        style={{ boxSizing: 'border-box', paddingLeft: 12 }}
      >
        <div style={{ transform: `translateX(${localPad}px)` }}>
          <div className="flex items-start gap-2">

            <div className="flex-none w-9 flex flex-col items-center text-sm text-slate-500">
              {!isRemoved ? (
                <>
                  <button
                    onClick={() => voteComment(comment.comment_id, 1)}
                    className={`hover:text-emerald-600 ${commentVotes?.[comment.comment_id] === 1 ? 'text-emerald-600 font-bold' : ''}`}
                  >▲</button>
                  <span className="text-slate-700 font-semibold">{comment.karma || 0}</span>
                  <button
                    onClick={() => voteComment(comment.comment_id, -1)}
                    className={`hover:text-rose-600 ${commentVotes?.[comment.comment_id] === -1 ? 'text-rose-600 font-bold' : ''}`}
                  >▼</button>
                </>
              ) : (
                // preserve layout but hide interactive controls when removed
                <span className="text-slate-400"> </span>
              )}
            </div>

            <div className="flex-1 min-w-0" style={{ overflowWrap: 'anywhere', wordBreak: 'break-word' }}>
              <div className="flex items-center justify-between">
                <div className="text-sm text-slate-500 mb-1 flex items-center">
                  <button
                    onClick={() => setCollapsed(!collapsed)}
                    aria-label={collapsed ? 'Expand comment' : 'Collapse comment'}
                    title={collapsed ? 'Expand' : 'Collapse'}
                    className="mr-2 w-6 h-6 text-xs flex items-center justify-center border rounded text-slate-500 hover:bg-slate-100"
                  >
                    {collapsed ? '+' : '−'}
                  </button>
                  {/* Profile picture to the left of username */}
                  <img
                    src={comment.image_url || comment.avatar || '/default-avatar.svg'}
                    alt={comment.username || 'User'}
                    className="h-7 w-7 rounded-full object-cover mr-2 border border-gray-200 bg-white"
                  />
                  {comment.username || comment.author_id} •{' '}
                  <time dateTime={comment.created_at}>{new Date(comment.created_at).toISOString().slice(0, 10)}</time>
                </div>
              </div>

            {collapsed ? (
              <div className="mt-2 italic text-sm text-slate-500">
                Comment collapsed{comment.replies?.length ? ` • ${countDescendants(comment)} repl${countDescendants(comment) === 1 ? 'y' : 'ies'} hidden` : ''}
                <button onClick={() => setCollapsed(false)} className="ml-2 text-sm underline">Expand</button>
              </div>
            ) : (
              <>
                {renderCommentText(comment.text)}

                <div className="mt-2 text-sm">
                  <button
                    onClick={() => setReplyingTo(replyingTo === comment.comment_id ? null : comment.comment_id)}
                    className="text-slate-600 hover:text-slate-900"
                  >
                    Reply
                  </button>
                  {currentUser && !isRemoved && (
                    (Number(currentUser.role_id) === 1 || Number(currentUser.id) === Number(comment.author_id) || Number(currentUser.id) === Number(threadAuthorId)) && (
                      <button onClick={() => deleteComment(comment.comment_id)} className="ml-4 text-slate-600 hover:text-slate-900">Delete</button>
                    )
                  )}
                </div>

                {replyingTo === comment.comment_id && (
                  <div className="mt-2">
                    <textarea autoFocus value={replyText} onChange={e => setReplyText(e.target.value)} className="w-full border rounded-md p-2" rows={3} placeholder={`Reply to ${comment.username || 'user'}...`} />
                    <div className="mt-2 flex gap-2">
                      <button onClick={() => submitReply(comment.comment_id)} className="px-3 py-1 bg-slate-900 text-white rounded-md">Post reply</button>
                      <button onClick={() => { setReplyingTo(null); setReplyText(''); }} className="px-3 py-1 border rounded-md">Cancel</button>
                    </div>
                  </div>
                )}
              </>
            )}
            
            </div>
          </div>

          {/* Replies container: render replies without nested list semantics; pass absolute pad to children */}
          {comment.replies?.length > 0 && !collapsed && (
            <div className="mt-3">
              {(depth + 1) >= COLLAPSE_AT && !expanded ? (
                <div className="flex items-center gap-2">
                  <button onClick={() => setExpanded(true)} className="text-sm text-slate-600 underline">Show {countDescendants(comment)} more repl{countDescendants(comment) === 1 ? 'y' : 'ies'}</button>
                </div>
              ) : (
                <div className="mt-4 space-y-3">
                  {comment.replies.map(r => (
                    <CommentNode
                      key={r.comment_id}
                      comment={r}
                      replyingTo={replyingTo}
                      setReplyingTo={setReplyingTo}
                      deleteComment={deleteComment}
                      replyText={replyText}
                      setReplyText={setReplyText}
                      submitReply={submitReply}
                      voteComment={voteComment}
                      commentVotes={commentVotes}
                      currentUser={currentUser}
                      threadAuthorId={threadAuthorId}
                      depth={depth + 1}
                      parentPad={padLeft}
                    />
                  ))}
                </div>
              )}

              {(depth + 1) >= COLLAPSE_AT && expanded && (
                <div className="mt-2">
                  <button onClick={() => setExpanded(false)} className="text-sm text-slate-500 underline">Hide replies</button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function ThreadView() {
  // App routes use /t/:slug/:id — second param is named `id` (may be numeric id or slug)
  const { id: idParam } = useParams();
  const identifier = idParam; // could be numeric id or thread slug
  const [thread, setThread] = useState(null);
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [text, setText] = useState('');
  const [error, setError] = useState(null);
  const [replyingTo, setReplyingTo] = useState(null); // comment_id being replied to
  const [replyText, setReplyText] = useState('');
  const [commentVotes, setCommentVotes] = useState({}); // comment_id -> 1|-1|0
  const { user: currentUser } = useAuth();
  const navigate = useNavigate();
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [subsLoading, setSubsLoading] = useState(false);
  // removed IntersectionObserver fallback — view POST is sent immediately on load

  // helper: flatten nested comments into a single array (includes replies)
  const flattenComments = (list) => {
    const out = [];
    if (!list || !Array.isArray(list)) return out;
    const walk = (items) => {
      items.forEach(it => {
        out.push(it);
        if (it.replies && Array.isArray(it.replies) && it.replies.length) walk(it.replies);
      });
    };
    walk(list);
    return out;
  };

  // helper: update karma for a comment id anywhere in the nested tree (returns new tree)
  const updateCommentKarma = (items, commentId, delta) => {
    if (!items || !Array.isArray(items)) return items;
    return items.map(it => {
      if (it.comment_id === commentId) {
        return { ...it, karma: (Number(it.karma) || 0) + delta };
      }
      if (it.replies && it.replies.length) {
        return { ...it, replies: updateCommentKarma(it.replies, commentId, delta) };
      }
      return it;
    });
  };

  // helper: set karma to an absolute value for a comment id anywhere in the nested tree
  const setCommentKarma = (items, commentId, value) => {
    if (!items || !Array.isArray(items)) return items;
    return items.map(it => {
      if (it.comment_id === commentId) {
        return { ...it, karma: value };
      }
      if (it.replies && it.replies.length) {
        return { ...it, replies: setCommentKarma(it.replies, commentId, value) };
      }
      return it;
    });
  };

  // resolve category id for thread (use thread fields or lookup by slug)
  async function resolveCategoryId(threadObj) {
    if (!threadObj) return null;
    const directId = threadObj.categories_id || threadObj.category_id || threadObj.categoryId || threadObj.categoryId;
    if (directId) return String(directId);
    const slug = threadObj.category_slug || threadObj.category_name || threadObj.category || threadObj.categorySlug;
    if (!slug) return null;
    // try to fetch categories and find matching slug
    try {
      const res = await fetch('/api/categories');
      if (!res.ok) return null;
      const ct = res.headers.get('content-type') || '';
      if (!ct.includes('application/json')) return null;
      const body = await res.json();
      const cats = body.categories || [];
      const found = cats.find(c => (c.slug && String(c.slug) === String(slug)) || (String(c.name) === String(slug)));
      if (found) return String(found.categories_id || found.category_id || found.id || found.slug);
    } catch (e) {
      return null;
    }
    return null;
  }

  // If an image is present and we measured its natural size, use that to adapt the left column width

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        if (!identifier) {
          setError('No thread identifier provided in URL');
          return;
        }

        // if identifier is numeric, fetch by id endpoint
        if (/^\d+$/.test(String(identifier))) {
          const res = await fetch(`/api/threads/${identifier}`);
          if (!res.ok) throw new Error(`${res.status} ${await res.text()}`);
          const body = await res.json();
          setThread(body.thread);
            try { await fetch(`/api/threads/${body.thread.thread_id}/view`, { method: 'POST', credentials: 'include' }); } catch (e) { }
          setComments(body.comments || []);
          // initialize comment votes from server if present (include nested replies)
          if (body.comments && Array.isArray(body.comments)) {
            const mapping = {};
            flattenComments(body.comments).forEach(c => {
              mapping[c.comment_id] = Number(c.user_vote ?? c.user_vote_value ?? c.current_user_vote ?? c.user_vote_by_current_user ?? 0) || 0;
            });
            setCommentVotes(mapping);
          }
          return;
        }

        // otherwise identifier is probably a slug — fetch thread list and find matching slug
        const listRes = await fetch('/api/threads');
        if (!listRes.ok) throw new Error(`${listRes.status} ${await listRes.text()}`);
        const listBody = await listRes.json();
        const threads = listBody.threads || [];
        const found = threads.find(t => (t.thread_slug === identifier) || (t.slug === identifier) || (t.slugified === identifier));
        if (!found) {
          setError('Thread not found');
          return;
        }
        // populate thread and fetch its comments using the numeric id
  setThread(found);
  try { await fetch(`/api/threads/${found.thread_id}/view`, { method: 'POST', credentials: 'include' }); } catch (e) {}
        const detail = await fetch(`/api/threads/${found.thread_id}`);
        if (!detail.ok) throw new Error(`${detail.status} ${await detail.text()}`);
        const detailBody = await detail.json();
        setComments(detailBody.comments || []);
        if (detailBody.comments && Array.isArray(detailBody.comments)) {
          const mapping = {};
          flattenComments(detailBody.comments).forEach(c => {
            mapping[c.comment_id] = Number(c.user_vote ?? c.user_vote_value ?? c.current_user_vote ?? c.user_vote_by_current_user ?? 0) || 0;
          });
          setCommentVotes(mapping);
        }
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [identifier]);

  // IntersectionObserver fallback removed — views are recorded immediately on load

  // when thread is set, check the current user's subscription to this thread's category
  useEffect(() => {
    if (!thread) return;
    let mounted = true;
    (async () => {
      setSubsLoading(true);
      try {
        const res = await fetch('/api/me/subscriptions', { credentials: 'include' });
        if (!res.ok) {
          if (mounted) setIsSubscribed(false);
          return;
        }
        const body = await res.json();
        const subs = body.subscriptions || [];
        const catId = await resolveCategoryId(thread);
        if (!catId) {
          const slug = thread.category_slug || thread.category_name;
          const matchBySlug = subs.some(s => String(s.slug) === String(slug) || String(s.category_slug) === String(slug));
          if (mounted) setIsSubscribed(Boolean(matchBySlug));
          return;
        }
        const found = subs.some(s => String(s.category_id || s.categories_id || s.categoryId) === String(catId) || String(s.category_id) === String(catId));
        if (mounted) setIsSubscribed(Boolean(found));
      } catch (e) {
        if (mounted) setIsSubscribed(false);
      } finally {
        if (mounted) setSubsLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, [thread]);

  

  // toggle subscription for the current thread's category
  async function toggleSubscription() {
    if (!currentUser) {
      navigate('/login');
      return;
    }
    if (!thread) return;
    setSubsLoading(true);
    try {
      let catId = await resolveCategoryId(thread);
      if (!catId) {
        // couldn't resolve id; abort
        console.error('Unable to resolve category id for subscription');
        setSubsLoading(false);
        return;
      }
      if (isSubscribed) {
        const res = await fetch(`/api/categories/${catId}/subscribe`, { method: 'DELETE', credentials: 'include' });
        if (res.ok) setIsSubscribed(false);
      } else {
        const res = await fetch(`/api/categories/${catId}/subscribe`, { method: 'POST', credentials: 'include' });
        if (res.ok) setIsSubscribed(true);
      }
    } catch (e) {
      console.error('Subscription toggle failed', e);
    } finally {
      setSubsLoading(false);
    }
  }

  const submitComment = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    try {
  const res = await fetch(`/api/threads/${thread.thread_id}/comments`, { method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ text }) });
      if (!res.ok) {
        // prefer structured JSON message if present, otherwise fall back to text
        let msg = 'Failed to post comment';
        try {
          const ct = res.headers.get('content-type') || '';
          if (ct.includes('application/json')) {
            const j = await res.json();
            msg = (j && (j.message || j.msg || j.error)) ? (j.message || j.msg || j.error) : JSON.stringify(j);
          } else {
            const t = await res.text();
            msg = t || msg;
          }
        } catch (e) {
          const t = await res.text().catch(() => null);
          if (t) msg = t;
        }
        throw new Error(msg);
      }
      // refresh comments
      const refresh = await fetch(`/api/threads/${thread.thread_id}`);
      const body = await refresh.json();
      setComments(body.comments || []);
      setText('');
    } catch (err) {
      setError(err.message);
    }
  };

  

  const submitReply = async (parentId) => {
    if (!replyText.trim()) return;
    try {
  const res = await fetch(`/api/threads/${thread.thread_id}/comments`, { method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ text: replyText, parent_id: parentId }) });
      if (!res.ok) {
        let msg = 'Failed to post reply';
        try {
          const ct = res.headers.get('content-type') || '';
          if (ct.includes('application/json')) {
            const j = await res.json();
            msg = (j && (j.message || j.msg || j.error)) ? (j.message || j.msg || j.error) : JSON.stringify(j);
          } else {
            const t = await res.text();
            msg = t || msg;
          }
        } catch (e) {
          const t = await res.text().catch(() => null);
          if (t) msg = t;
        }
        throw new Error(msg);
      }
      // refresh
      const refresh = await fetch(`/api/threads/${thread.thread_id}`);
      const body = await refresh.json();
      setComments(body.comments || []);
      setReplyText('');
      setReplyingTo(null);
    } catch (err) {
      setError(err.message);
    }
  };

  const voteComment = async (commentId, value) => {
    // toggle behavior: if user already voted same value, remove vote (send 0)
    const current = commentVotes[commentId] || 0;
    const toSend = current === value ? 0 : value;
    // compute optimistic delta
    const delta = toSend === 0 ? -current : (current === 0 ? value : (value - current));

    // optimistic update (supports nested replies)
    setComments(prev => updateCommentKarma(prev, commentId, delta));
    setCommentVotes(prev => ({ ...prev, [commentId]: toSend }));

    try {
  const res = await fetch(`/api/threads/comments/${commentId}/vote`, { method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ value: toSend }) });
      if (!res.ok) {
        // prefer structured JSON message if present, otherwise fall back to text
        let msg = 'Vote failed';
        try {
          const ct = res.headers.get('content-type') || '';
          if (ct.includes('application/json')) {
            const j = await res.json();
            msg = (j && (j.message || j.msg || j.error)) ? (j.message || j.msg || j.error) : JSON.stringify(j);
          } else {
            const t = await res.text();
            msg = t || msg;
          }
        } catch (e) {
          const t = await res.text().catch(() => null);
          if (t) msg = t;
        }
        throw new Error(msg);
      }
      const body = await res.json();
      // reconcile authoritative karma (supports nested replies)
      setComments(prev => setCommentKarma(prev, commentId, body.karma));
    } catch (err) {
      // on error, rollback optimistic (simple strategy: refetch comments)
      const refresh = await fetch(`/api/threads/${thread.thread_id}`);
      const b = await refresh.json();
      setComments(b.comments || []);
      setError(err.message || 'Vote failed');
    }
  };

  const deleteComment = async (commentId) => {
    // ask for confirmation first
    const ok = window.confirm('Remove this comment? This cannot be undone.');
    if (!ok) return;
    try {
      const res = await fetch(`/api/threads/comments/${commentId}`, { method: 'DELETE', credentials: 'include' });
      if (!res.ok) {
        let msg = 'Delete failed';
        try {
          const ct = res.headers.get('content-type') || '';
          if (ct.includes('application/json')) {
            const j = await res.json();
            msg = (j && (j.message || j.msg || j.error)) ? (j.message || j.msg || j.error) : JSON.stringify(j);
          } else {
            const t = await res.text();
            msg = t || msg;
          }
        } catch (e) {
          const t = await res.text().catch(() => null);
          if (t) msg = t;
        }
        throw new Error(msg);
      }
      // refresh comments after delete
      const refresh = await fetch(`/api/threads/${thread.thread_id}`);
      const body = await refresh.json();
      setComments(body.comments || []);
    } catch (err) {
      setError(err.message || 'Unable to delete comment');
    }
  };

  // compute derived stats (include nested replies)
  const totalCommentVotes = flattenComments(comments).reduce((s, c) => s + (Number(c.karma) || 0), 0);
  const viewCount = thread ? (thread.view_count ?? thread.viewCount ?? thread.views ?? 0) : 0;
  const threadKarma = thread ? (thread.karma || 0) : 0;

  if (loading) return <div className="p-6">Loading...</div>;
  if (error) return <div className="p-6 text-rose-600">Error: {error}</div>;
  if (!thread) return <div className="p-6">Thread not found</div>;

  return (
    <main className="w-full max-w-7xl mx-auto p-6">
      <div className="flex flex-col md:flex-row md:items-start gap-6">
      <article className="bg-white border border-slate-200 p-6 rounded-md md:w-1/2">
          <div className="flex items-center gap-3 mb-2">
            <h1 className="text-2xl font-semibold">{thread.title}</h1>
            {/* Delete thread button - visible to thread author or admins */}
            {currentUser && (Number(currentUser.role_id) === 1 || Number(currentUser.id) === Number(thread.author_id)) && (
              <button onClick={async () => {
                const ok = window.confirm('Delete this thread and all related comments, replies, media, and votes? This cannot be undone.');
                if (!ok) return;
                try {
                  const res = await fetch(`/api/threads/${thread.thread_id}`, { method: 'DELETE', credentials: 'include' });
                  if (!res.ok) {
                    let msg = 'Failed to delete thread';
                    try {
                      const ct = res.headers.get('content-type') || '';
                      if (ct.includes('application/json')) {
                        const j = await res.json();
                        msg = (j && (j.message || j.msg || j.error)) ? (j.message || j.msg || j.error) : JSON.stringify(j);
                      } else {
                        const t = await res.text();
                        msg = t || msg;
                      }
                    } catch (e) { }
                    throw new Error(msg);
                  }
                  // navigate back to category or home
                  const catSlug = thread.category_slug || thread.category_name || 't';
                  navigate(`/t/${catSlug}`);
                } catch (err) {
                  setError(err.message || 'Unable to delete thread');
                }
              }} className="ml-2 px-2 py-1 text-xs rounded bg-rose-50 text-rose-700 border border-rose-100">Delete Thread</button>
            )}
          </div>
          <div className="text-sm text-slate-500 mb-4 flex items-center gap-2">
            <img
              src={thread.image_url || '/default-avatar.svg'}
              alt={thread.author || 'User'}
              className="w-7 h-7 rounded-full object-cover border border-slate-200 bg-white"
              style={{ minWidth: 28, minHeight: 28 }}
            />
            <span>by {thread.author}</span>
            <span>•</span>
            <time dateTime={thread.created_at || thread.createdAt}>{new Date(thread.created_at || thread.createdAt).toISOString().slice(0,10)}</time>
            <span>•</span>
            <span className="text-blue-500 ml-1"><Link to={`/t/${thread.category_slug || thread.category_name || 'uncategorized'}`}>t/{thread.category_name || 'Uncategorized'}</Link></span>
            <button onClick={toggleSubscription} disabled={subsLoading} className={`ml-3 px-2 py-1 text-xs rounded ${isSubscribed ? 'bg-red-50 text-red-700' : 'bg-slate-100 text-slate-700'}`}>
              {subsLoading ? '...' : (isSubscribed ? 'Unsubscribe' : 'Subscribe')}
            </button>
          </div>
          <div className="prose max-w-full">{renderBody(thread.body_text, thread.media)}</div>
          <br/>
          <div>
          <div className="bg-white border border-slate-200 p-4 rounded-md md:overflow-auto">
            <h2 className="text-lg font-bold mb-3">Thread stats</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-slate-700">
              <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-md shadow-sm">
                <i class="fa-solid fa-eye w-6 h-6 text-slate-500 flex-none"></i>
                <div className="flex-1">
                  <div className="text-xs text-slate-500">Views</div>
                  <div className="text-lg font-semibold text-slate-900">{viewCount}</div>
                </div>
              </div>

              <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-md shadow-sm">
                <i class="fa-solid fa-arrow-up w-6 h-6 text-slate-500 flex-none"></i>
                <div className="flex-1">
                  <div className="text-xs text-slate-500">Total Thread Karma</div>
                  <div className="text-lg font-semibold text-slate-900">{threadKarma}</div>
                </div>
              </div>

              <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-md shadow-sm">
                <i class="fa-solid fa-comment w-6 h-6 text-slate-500 flex-none"></i>
                <div className="flex-1">
                  <div className="text-xs text-slate-500">Total Comments</div>
                  <div className="text-lg font-semibold text-slate-900">{thread.comment_count || 0}</div>
                </div>
              </div>

              <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-md shadow-sm">
                <i class="fa-solid fa-arrow-up w-6 h-6 text-slate-500 flex-none"></i>
                <div className="flex-1">
                  <div className="text-xs text-slate-500">Total Comment Votes</div>
                  <div className="text-lg font-semibold text-slate-900">{totalCommentVotes}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
        </article>


        <aside className="mt-4 md:mt-0 md:w-1/2">
           <div className="bg-white border border-slate-200 p-4 rounded-md md:h-[calc(100vh-4rem)] md:overflow-auto ">
            <h2 className="text-lg font-bold mb-2 ">Comments</h2>
            <form onSubmit={submitComment} className="space-y-2">
              <textarea value={text} onChange={e => setText(e.target.value)} className="w-full h-10 border rounded-md p-2" rows={4} placeholder="Add a comment..." />
              <div>
                <button className="px-4 py-2 bg-slate-900 text-white rounded-md">Post comment</button>
              </div>
            </form>

            <div className="mt-4 space-y-3 max-h-[60vh] overflow-auto">
              {comments.map(c => (
                <CommentNode key={c.comment_id} comment={c} replyingTo={replyingTo} setReplyingTo={setReplyingTo} deleteComment={deleteComment} replyText={replyText} setReplyText={setReplyText} submitReply={submitReply} voteComment={voteComment} commentVotes={commentVotes} currentUser={currentUser} threadAuthorId={thread && (thread.author_id || thread.authorId)} />
              ))}
            </div>
          </div>
        </aside>
      </div>
    </main>
  );
}

export default ThreadView;